import { TaskStatus, type Task } from '@/types/models';

/**
 * The Tasks page's URL-driven modes (2.6.0 follow-up).
 *
 *   /tasks                        every task, calendar on web (the original page)
 *   /tasks?filter=follow-ups      only work-order follow-ups, LIST view
 *
 * The sidebar's "Follow Up Tasks" row links to the second. Follow-ups are the tasks the
 * work-order pipeline created (`workOrderId` set); a property manager chasing "go back to
 * WO #31198" wants a list of those, not a month grid of every recurring inspection.
 */

export type TasksViewMode = 'list' | 'calendar';
export type TasksFilter = 'all' | 'follow-ups';

const one = (raw: string | string[] | undefined): string | undefined => (Array.isArray(raw) ? raw[0] : raw);

export function tasksFilterFromParam(raw: string | string[] | undefined): TasksFilter {
	return one(raw) === 'follow-ups' ? 'follow-ups' : 'all';
}

/**
 * Opening view. An explicit `?view=` wins; otherwise follow-ups open as a list everywhere,
 * and the unfiltered page keeps its calendar-on-web default.
 */
export function initialTasksView(
	params: { view?: string | string[]; filter?: string | string[] },
	isWeb: boolean,
): TasksViewMode {
	const view = one(params.view);
	if (view === 'list' || view === 'calendar') return view;
	if (tasksFilterFromParam(params.filter) === 'follow-ups') return 'list';
	return isWeb ? 'calendar' : 'list';
}

/**
 * A follow-up is a task the work-order pipeline created for a flagged work order (#48).
 * The API tags them `task_list = 'follow_up'` and links the work order; either marker
 * counts, so a hand-made task pointed at a work order is a follow-up too.
 */
export const isFollowUpTask = (task: Pick<Task, 'workOrderId' | 'taskList'>): boolean =>
	task.taskList === 'follow_up' || !!task.workOrderId;

export function applyTasksFilter<T extends Pick<Task, 'workOrderId' | 'taskList'>>(tasks: T[], filter: TasksFilter): T[] {
	return filter === 'follow-ups' ? tasks.filter(isFollowUpTask) : tasks;
}

/**
 * Still to do. The list hides everything else by default — a finished follow-up sitting
 * between two open ones is noise, and "archived" exists precisely so it stops showing up.
 * Cancelled goes with them: nobody is going to act on it.
 */
export const isOpenTask = (task: Pick<Task, 'status'>): boolean =>
	task.status !== TaskStatus.Completed
	&& task.status !== TaskStatus.Cancelled
	&& task.status !== TaskStatus.Archived;
